// SignalSourceEditor — the signal-definition editor shared by the Library
// signal page (right rail) and the Explore signal-definition panel.
//
// A signal is usually ONE python script with identity around it, so the
// common case gets first-class fields — command / title / description
// inputs and a CodeMirror python block for the step body — instead of a
// raw JSON textarea. Definitions the fields can't express (multi-step,
// lookup steps, non-python kinds) fall back to a CodeMirror JSON editor
// of the whole object, and the mode toggle lets you drop to JSON
// deliberately even when fields would do.
//
// Controlled: `value` is the definition OBJECT (sans id/timestamps —
// hosts strip those). Edits call onChange with the next object, or null
// while the JSON fallback text is unparsable so hosts can disable Save.
// Unknown top-level keys and extra step fields survive a structured
// edit: the rebuild spreads the previous object rather than
// reconstructing it from the fields.
//
// Hosts should key this component on the signal id so switching signals
// remounts with fresh internal state (mode + fallback text).

import { useEffect, useRef, useState } from "react";
import CodeEditor from "./CodeEditor.jsx";
import { NOTE_CLASS } from "../lib/ui.js";

const FIELD_INPUT = "min-h-[28px] w-full rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)] read-only:text-[color:var(--muted)] read-only:cursor-default";
const MODE_BTN = "rounded-[2px] border px-[6px] py-[2px] text-[9px] leading-[1.2] tracking-[0.06em] uppercase transition-colors";

// Fields mode can express exactly the one-python-step shape. Anything
// else (no steps, several steps, a lookup step) edits as JSON.
export function signalFieldsEligible(def) {
  return Boolean(
    def
      && Array.isArray(def.steps)
      && def.steps.length === 1
      && def.steps[0]?.kind === "python"
      && typeof def.steps[0].code === "string",
  );
}

export default function SignalSourceEditor({ value, onChange, readOnly = false }) {
  const eligible = signalFieldsEligible(value);
  const [modePref, setModePref] = useState("fields");
  // JSON fallback text is internal state — the raw string must survive
  // unparsable intermediate keystrokes that can't round-trip through
  // the object prop.
  const [jsonText, setJsonText] = useState(() => JSON.stringify(value ?? {}, null, 2));

  // Distinguish our own edits echoing back through the host from a
  // genuinely external value change (host reseeds the draft — switching
  // signals, a save echo, a fork). report() records every object we
  // hand out; when `value` arrives without having passed through it,
  // re-seed the JSON text. Without this, a host that reseeds its draft
  // an effect-tick after remounting us (multi-step signals surfaced it:
  // the stale mount value was fields-eligible, so jsonText seeded for
  // the WRONG def and the real one rendered an empty JSON document).
  const lastReportedRef = useRef(value);
  const report = (next) => {
    lastReportedRef.current = next;
    onChange?.(next);
  };
  useEffect(() => {
    if (value === lastReportedRef.current) return;
    lastReportedRef.current = value;
    setJsonText(JSON.stringify(value ?? {}, null, 2));
  }, [value]);

  const mode = eligible && modePref === "fields" ? "fields" : "json";

  const step = eligible ? value.steps[0] : null;

  const setField = (key, v) => report({ ...value, [key]: v });
  const setCode = (code) => report({ ...value, steps: [{ ...step, code }] });

  const enterJson = () => {
    setJsonText(JSON.stringify(value ?? {}, null, 2));
    setModePref("json");
  };
  const enterFields = () => {
    // Only reachable when the current object is eligible — i.e. the
    // JSON text parses (onChange has the object) and has the one-step
    // shape. The button below is disabled otherwise.
    setModePref("fields");
  };

  return (
    // minmax(0,1fr) is load-bearing: the CodeMirror children report
    // their longest line as max-content width, and an auto-sized grid
    // track would adopt it and blow the editor past the host column.
    <div className="grid min-w-0 content-start gap-[8px] [grid-template-columns:minmax(0,1fr)]">
      <div className="flex min-w-0 items-start gap-[4px]">
        <span className={`${NOTE_CLASS} min-w-0 flex-1`}>
          {mode === "fields"
            ? "The script runs over one hydrated prompt bound as params[“event”]; its output is the annotation (null = didn’t fire)."
            : "Definition (JSON). Steps run over one hydrated prompt bound as params[“event”]; the terminal step’s output is the annotation (null = didn’t fire)."}
        </span>
        <button
          type="button"
          onClick={enterFields}
          disabled={!eligible}
          title={eligible ? undefined : "Only single-python-step signals edit as fields"}
          className={[
            MODE_BTN,
            "shrink-0",
            mode === "fields"
              ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
              : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-40 disabled:hover:text-[color:var(--muted)] cursor-pointer disabled:cursor-default",
          ].join(" ")}
        >
          Fields
        </button>
        <button
          type="button"
          onClick={enterJson}
          className={[
            MODE_BTN,
            "shrink-0",
            mode === "json"
              ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
              : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer",
          ].join(" ")}
        >
          JSON
        </button>
      </div>

      {mode === "fields" ? (
        <>
          <div className="grid gap-[8px] [grid-template-columns:minmax(0,1fr)_minmax(0,1.4fr)]">
            <label className="grid content-start gap-[4px]">
              <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Command</span>
              <input
                value={value.command || ""}
                onChange={(e) => setField("command", e.target.value)}
                readOnly={readOnly}
                spellCheck={false}
                className={`${FIELD_INPUT} font-mono`}
              />
            </label>
            <label className="grid content-start gap-[4px]">
              <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Title</span>
              <input
                value={value.title || ""}
                onChange={(e) => setField("title", e.target.value)}
                readOnly={readOnly}
                className={FIELD_INPUT}
              />
            </label>
          </div>
          <label className="grid gap-[4px]">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Description</span>
            <textarea
              value={value.description || ""}
              onChange={(e) => setField("description", e.target.value)}
              readOnly={readOnly}
              rows={3}
              className="w-full resize-y rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[6px] text-[12px] leading-[1.45] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)] read-only:text-[color:var(--muted)] read-only:cursor-default"
            />
          </label>
          <div className="grid min-w-0 gap-[4px] [grid-template-columns:minmax(0,1fr)]">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Python</span>
            <CodeEditor
              language="python"
              value={step.code || ""}
              onChange={setCode}
              readOnly={readOnly}
              autoHeight
              className="min-w-0 max-w-full"
              minHeight="220px"
              maxHeight="70vh"
            />
          </div>
        </>
      ) : (
        <CodeEditor
          language="json"
          value={jsonText}
          onChange={(text) => {
            setJsonText(text);
            try {
              report(JSON.parse(text));
            } catch {
              report(null); // hosts disable Save while unparsable
            }
          }}
          readOnly={readOnly}
          autoHeight
          className="min-w-0 max-w-full"
          minHeight="320px"
          maxHeight="70vh"
        />
      )}
    </div>
  );
}
